import React, { useState, useMemo, useEffect } from 'react';
import { GoogleMap, GroundOverlay, Polygon } from '@react-google-maps/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  ChevronLeft,
  ChevronRight,
  Info,
  BarChart3,
  DivideSquare,
} from 'lucide-react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';

const ALL_MONTHS_ORDER = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatKey = (key) =>
  key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const STAT_CONFIG = [
  { key: 'average', label: 'Average' },
  { key: 'median', label: 'Median' },
  { key: 'max', label: 'Max' },
  { key: 'min', label: 'Min' },
  { key: 'std', label: 'Std Dev' },
  { key: 'cloud', label: 'Cloud Cover' },
];

const STAT_DESCRIPTIONS = {
  average: 'Mean vegetation index value for the selected month',
  median: 'Middle value of all pixel indices',
  max: 'Maximum observed index value',
  min: 'Minimum observed index value',
  std: 'Standard deviation of index values',
  cloud: 'Percentage cloud cover in the scene',
};

const getColor = (value) => {
  if (value >= 0.9) return '#06653d';
  if (value >= 0.8) return '#11a75f';
  if (value >= 0.7) return '#81bf6c';
  if (value >= 0.6) return '#bae383';
  if (value >= 0.5) return '#e6f3a4';
  if (value >= 0.4) return '#fff0b5';
  if (value >= 0.3) return '#fbc07e';
  if (value >= 0.2) return '#f7885a';
  if (value >= 0.1) return '#ea4f3b';
  return '#ab0535';
};

const getMetricColor = (metric) => (metric === 'ndvi' ? '#16a34a' : '#2563eb');
const getLightMetricColor = (metric) =>
  metric === 'ndvi' ? '#beeb1c' : '#eb9a21';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '12px',
};

const RiskTypeViewer = ({ data, coordinates, riskType = 'POST' }) => {
  const [activeYear, setActiveYear] = useState(null);
  const [activeMetric, setActiveMetric] = useState('ndvi');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState('stats');
  const [isShow, setIsShow] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const polygonCoords = coordinates[0].map(([lng, lat]) => ({ lat, lng }));
  const center = polygonCoords.reduce(
    (acc, cur) => {
      acc.lat += cur.lat;
      acc.lng += cur.lng;
      return acc;
    },
    { lat: 0, lng: 0 }
  );
  center.lat /= polygonCoords.length;
  center.lng /= polygonCoords.length;

  useEffect(() => {
    if (!data) return;
    const years = Object.keys(data).sort((a, b) => Number(b) - Number(a));
    const latestYear = years[0];
    if (latestYear) {
      setActiveYear(latestYear);
      const yearData = data[latestYear];
      const validMonths = Object.keys(yearData)
        .filter((k) => ALL_MONTHS_ORDER.includes(k))
        .sort(
          (a, b) => ALL_MONTHS_ORDER.indexOf(a) - ALL_MONTHS_ORDER.indexOf(b)
        );
      if (validMonths.length > 0) setSelectedMonth(validMonths[0]);
    }
  }, [data]);

  const {
    chartData,
    splitOffset,
    currentMonthStats,
    currentImage,
    yearType,
    yieldPred,
    yieldDev,
    cvma,
    anomalies,
    bounds,
    currentAverage,
    cvmaTrend,
    trendAnamoly,
    alpi_score,
  } = useMemo(() => {
    if (!activeYear || !data[activeYear])
      return { chartData: [], currentMonthStats: {}, splitOffset: 0 };

    const yearData = data[activeYear];
    const type = yearData.type || 'history';

    const validMonths = Object.keys(yearData)
      .filter((key) => ALL_MONTHS_ORDER.includes(key))
      .sort(
        (a, b) => ALL_MONTHS_ORDER.indexOf(a) - ALL_MONTHS_ORDER.indexOf(b)
      );

    // 1. Calculate Gradient Offset
    // Find the index where prediction starts to switch colors
    const firstPredIndex = validMonths.findIndex(
      (m) => yearData[m]?.[activeMetric]?.indices?.predicted === true
    );
    // If no prediction, offset is 100% (all solid). If all prediction, 0%.
    const splitOffset =
      firstPredIndex === -1 ? 1 : firstPredIndex / (validMonths.length - 1);

    const chartData = validMonths.map((m) => {
      const baseline = yearData.history_averages?.[m]?.[activeMetric] ?? null;
      const monthObj = yearData[m]?.[activeMetric] || {};
      const indices = monthObj.indices || {};
      const val = indices.average;
      const isPredicted = indices.predicted === true;

      return {
        name: m,
        baseline: riskType === 'POST' && type === 'current' ? baseline : null,
        val, // SINGLE VALUE FOR CONTINUOUS LINE
        isPredicted,
      };
    });

    const effectiveMonth = validMonths.includes(selectedMonth)
      ? selectedMonth
      : validMonths[0];
    const activeMonthData = yearData[effectiveMonth] || {};
    const metricData = activeMonthData[activeMetric] || {};

    let mapBounds = null;
    if (coordinates && coordinates.length > 0) {
      const lats = coordinates[0].map((c) => c[1]);
      const lngs = coordinates[0].map((c) => c[0]);
      mapBounds = {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs),
      };
    }

    return {
      chartData,
      splitOffset,
      currentMonthStats: metricData?.indices || {},
      currentImage: metricData?.image,
      yearType: type,
      yieldPred: yearData?.yield_prediction,
      trendAnomaly: yearData?.trend_anomaly?.[activeMetric] ?? '',
      cvmaTrend: yearData?.cvma_trend,
      yieldDev: yearData?.yield_deviation,
      cvma: activeMonthData?.cvma,
      anomalies: metricData?.indices?.anomaly,
      alpi_score: yearData?.alpi_score,
      bounds: mapBounds,
      currentAverage: metricData?.indices?.average,
    };
  }, [data, activeYear, activeMetric, selectedMonth, riskType, coordinates]);

  const handleYearChange = (direction) => {
    const years = Object.keys(data).sort();
    const idx = years.indexOf(activeYear);
    if (direction === 'prev' && idx > 0) setActiveYear(years[idx - 1]);
    if (direction === 'next' && idx < years.length - 1)
      setActiveYear(years[idx + 1]);
  };

  const onLoad = React.useCallback(
    function callback(map) {
      if (bounds) {
        const boundsLiteral = new window.google.maps.LatLngBounds(
          { lat: bounds.south, lng: bounds.west },
          { lat: bounds.north, lng: bounds.east }
        );
        map.fitBounds(boundsLiteral);
      }
      setMapInstance(map);
    },
    [bounds]
  );

  if (!activeYear)
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading...
      </div>
    );

  const legendStyle = {
    position: 'absolute',
    bottom: isMobile ? 0 : 2,
    left: isMobile ? 1 : 2,
    background: 'rgba(255,255,255,0.95)',
    padding: isMobile ? '6px' : '12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
    // gap: isMobile ? "2px" : "0px",
    fontSize: isMobile ? '10px' : '12px',
    width: isMobile ? '140px' : '150px',
    maxWidth: isMobile ? '25%' : '90%',
    maxHeight: isMobile ? '80%' : '85dvh',
    height: isMobile ? '75%' : '70dvh',
    overflowY: 'auto',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    zIndex: 10,
    wordBreak: 'break-word',
  };

  const date =
    data?.[activeYear]?.[selectedMonth]?.[activeMetric]?.indices?.date ?? '';

  const metricLabels = {
    NDVI: 'Vegetation',
    NDMI: 'Moisture',
  };

  const labelName = metricLabels[activeMetric?.toUpperCase()] || activeMetric;

  return (
    <div style={styles.container}>
      {/* BACKGROUND MAP */}
      <div style={styles.backgroundLayer}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={13}
          onLoad={onLoad}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: true,
            mapTypeControlOptions: {
              style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
              position: window.google.maps.ControlPosition.TOP_RIGHT,
              mapTypeIds: ['roadmap', 'satellite'],
            },
          }}
        >
          {isShow && (
            <>
              {currentImage && bounds ? (
                <GroundOverlay
                  key={currentImage}
                  url={currentImage}
                  bounds={bounds}
                  opacity={0.8}
                />
              ) : (
                <Polygon
                  paths={polygonCoords}
                  options={{
                    fillColor: getColor(currentAverage),
                    fillOpacity: 0.6,
                    strokeColor: 'black',
                    strokeOpacity: 1,
                    strokeWeight: 0.5,
                  }}
                />
              )}
            </>
          )}
        </GoogleMap>
      </div>

      {/* HEADER */}
      <div
        style={{
          ...styles.topHeader,
          top: isMobile ? '4rem' : '1rem',
          left:'50%',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '8px' : '16px',
          scale: isMobile ? '0.9' : '1',
        }}
      >
        <div style={styles.controlGroup}>
          <button
            onClick={() => handleYearChange('prev')}
            style={styles.navButton}
          >
            <ChevronLeft size={20} />
          </button>
          <span style={styles.yearText}>{activeYear}</span>
          <button
            onClick={() => handleYearChange('next')}
            style={styles.navButton}
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <div style={styles.controlGroup}>
          <button
            onClick={() => setActiveMetric('ndvi')}
            style={{
              ...styles.metricButton,
              backgroundColor:
                activeMetric === 'ndvi' ? '#16a34a' : 'transparent',
              color: activeMetric === 'ndvi' ? 'white' : '#4b5563',
            }}
          >
            Vegetation
          </button>
          <button
            onClick={() => setActiveMetric('ndmi')}
            style={{
              ...styles.metricButton,
              backgroundColor:
                activeMetric === 'ndmi' ? '#2563eb' : 'transparent',
              color: activeMetric === 'ndmi' ? 'white' : '#4b5563',
            }}
          >
            Moisture
          </button>
        </div>
      </div>

      {isMobile && (
        <div
          style={{
            position: 'absolute',
            top: '1.5rem',
            left: '48%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
            background: 'white',
            borderRadius: '8px',
            padding: '2px 8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
            scale: '0.8',
          }}
        >
          <select
            value={mobileView}
            onChange={(e) => setMobileView(e.target.value)}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              fontWeight: '600',
              background: 'transparent',
            }}
          >
            <option value="stats">📊 Stats</option>
            <option value="graph">📈 Graph</option>
          </select>
        </div>
      )}

      {/* STATS PANEL */}
      {(!isMobile || mobileView === 'stats') && (
        <>
          <div style={legendStyle}>
            <div style={styles.panelHeader}>
              <h3 style={styles.panelTitle}>
                Stats: {selectedMonth} {activeYear}
              </h3>
              {date && (
                <p style={styles.panelDate}>
                  <span className="fw-bold"> Date:</span> {date}
                </p>
              )}
              {/* <Info size={14} color="#9ca3af" /> */}
            </div>
            <div style={styles.statsList}>
              {STAT_CONFIG.map(({ key, label }) => {
                const val = currentMonthStats[key];
                if (val === undefined || val === null) return null;

                const max = key === 'cloud' ? 100 : 1;
                const pct = Math.min((Math.abs(val) / max) * 100, 100);
                const color =
                  key === 'cloud' ? '#94a3b8' : getMetricColor(activeMetric);

                return (
                  <div key={key} style={{ marginBottom: '6px' }}>
                    {/* Label + Info + Range */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontWeight: 'bold',
                        fontSize: isMobile ? '10px' : '12px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          justifyContent: 'space-between',
                        }}
                        className="w-100"
                      >
                        <div>{label}</div>

                        {STAT_DESCRIPTIONS[key] && (
                          <OverlayTrigger
                            trigger="click"
                            placement="top"
                            rootClose
                            overlay={
                              <Popover id={`popover-${key}`}>
                                <Popover.Header as="h3">{label}</Popover.Header>
                                <Popover.Body>
                                  {STAT_DESCRIPTIONS[key]}
                                </Popover.Body>
                              </Popover>
                            }
                          >
                            <div style={{ cursor: 'pointer', lineHeight: 1 }}>
                              <FaInfoCircle size={12} color="#555" />
                            </div>
                          </OverlayTrigger>
                        )}
                      </div>

                      {/* <span>{val.toFixed(3)}</span> */}
                    </div>

                    {/* Progress bar */}
                    <div
                      style={{
                        background: '#e5e7eb',
                        height: '8px',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        marginTop: '2px',
                      }}
                    >
                      <div
                        style={{
                          width: `${pct}%`,
                          height: '100%',
                          background: getColor(val.toFixed(3)),
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>

                    {/* Range text */}
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#6b7280',
                        marginTop: '2px',
                      }}
                    >
                      {val.toFixed(3)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div
              style={{
                ...styles.riskContainer,
                maxHeight: isMobile ? '150px' : '400px',
                //   overflowY: "scroll",
              }}
            >
              <div style={styles.riskCard}>
                <div style={styles.riskLabel}>Anomaly ({labelName})</div>
                <div
                  style={{
                    ...styles.riskValue,
                    color: anomalies < 0 ? '#ef4444' : '#16a34a',
                  }}
                >
                  {anomalies
                    ? `${anomalies > 0 ? '' : ''}${(anomalies * 100).toFixed(2)}%`
                    : 'N/A'}
                </div>
              </div>
              {cvma !== undefined && (
                <div style={styles.riskCard}>
                  <div style={styles.riskLabel}>CVMA Score</div>
                  <div
                    style={{
                      ...styles.riskValue,
                      color: cvma < 0 ? '#ef4444' : '#16a34a',
                    }}
                  >
                    {cvma.toFixed(2)}
                  </div>
                </div>
              )}
              {yearType === 'current' && (
                <div
                  style={{
                    ...styles.riskCard,
                    borderColor: '#e0e7ff',
                    backgroundColor: '#eef2ff',
                  }}
                >
                  <div style={{ ...styles.riskLabel, color: '#4f46e5' }}>
                    Trend Anamoly
                  </div>
                  <div
                    style={{
                      ...styles.riskValue,
                      color: trendAnamoly < 0 ? '#dc2626' : '#4338ca',
                    }}
                  >
                    {trendAnamoly ? `${trendAnamoly.toFixed(2)} %` : 'N/A'}
                  </div>
                </div>
              )}
              {yearType === 'current' && (
                <div
                  style={{
                    ...styles.riskCard,
                    borderColor: '#e0e7ff',
                    backgroundColor: '#eef2ff',
                  }}
                >
                  <div style={{ ...styles.riskLabel, color: '#4f46e5' }}>
                    Yield Deviation
                  </div>
                  <div
                    style={{
                      ...styles.riskValue,
                      color: yieldDev < 0 ? '#dc2626' : '#4338ca',
                    }}
                  >
                    {yieldDev ? `${yieldDev.toFixed(2)} %` : 'N/A'}
                  </div>
                </div>
              )}
              {yearType === 'current' && (
                <div
                  style={{
                    ...styles.riskCard,
                    borderColor: '#e0e7ff',
                    backgroundColor: '#eef2ff',
                  }}
                >
                  <div style={{ ...styles.riskLabel, color: '#4f46e5' }}>
                    Yield Prediction
                  </div>
                  <div
                    style={{
                      ...styles.riskValue,
                      color: yieldPred < 0 ? '#dc2626' : '#4338ca',
                    }}
                  >
                    {yieldPred ? `${yieldPred.toFixed(2)} t/h` : 'N/A'}
                  </div>
                </div>
              )}
              {yearType === 'current' && (
                <div
                  style={{
                    ...styles.riskCard,
                    borderColor: '#e0e7ff',
                    backgroundColor: '#eef2ff',
                  }}
                >
                  <div style={{ ...styles.riskLabel, color: '#4f46e5' }}>
                    CVMA Trend
                  </div>
                  <div
                    style={{
                      ...styles.riskValue,
                      color: cvmaTrend < 0 ? '#dc2626' : '#4338ca',
                    }}
                  >
                    {cvmaTrend ? `${cvmaTrend.toFixed(2)}` : 'N/A'}
                  </div>
                </div>
              )}
              {yearType === 'current' && (
                <div
                  style={{
                    ...styles.riskCard,
                    borderColor: '#e0e7ff',
                    backgroundColor: '#eef2ff',
                  }}
                >
                  <div style={{ ...styles.riskLabel, color: '#4f46e5' }}>
                    ALPI
                  </div>
                  <div
                    style={{
                      ...styles.riskValue,
                      color: alpi_score < 0 ? '#ef4444' : '#16a34a',
                    }}
                  >
                    {alpi_score !== undefined && alpi_score !== null
                      ? `${alpi_score.toFixed(2)} %`
                      : 'N/A'}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              position: 'absolute',
              width: '30%',
              bottom: isMobile ? '5px' : '10px',
              left: isMobile ? '25%' : '20%',
              //   right: isMobile ? "0" : "25%",
              //   transform: "translateX(-50%)",
              zIndex: 20,
              // background: "rgba(255, 255, 255, 0.9)",
              padding: isMobile ? '4px' : '8px',
              // borderRadius: "8px",
              // boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
              scale: isMobile ? '1' : '0.7',
            }}
          >
            <img
              src="../images/analysis_scale.png"
              alt="Legend"
              style={{
                width: isMobile ? '100%' : '100%',
                height: 'auto',
                objectFit: 'contain',
                marginLeft: isMobile ? '5%' : '0%',
              }}
            />
          </div>
        </>
      )}

      {/* GRAPH PANEL */}
      {(!isMobile || mobileView === 'graph') && (
        <div
          style={{
            ...styles.graphPanel,
            width: isMobile ? '250px' : '35%',
            left: isMobile ? '1%' : 'auto',
            right: isMobile ? 'auto' : '60px',
            bottom: isMobile ? '-4%' : '16px',
            height: isMobile ? '220px' : '260px',
            // scale: isMobile ? "0.8" : "1",
          }}
        >
          <div style={styles.graphHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BarChart3 size={16} color="#64748b" />
              <span style={styles.graphTitle}>{labelName} Trend</span>
            </div>
            <div style={styles.legendContainer}>
              {riskType === 'POST' && yearType === 'current' && (
                <div style={styles.legendItem}>
                  <div
                    style={{ ...styles.legendDot, backgroundColor: '#000000' }}
                  ></div>
                  <span>Avg</span>
                </div>
              )}
              <div style={styles.legendItem}>
                <div
                  style={{
                    ...styles.legendDot,
                    backgroundColor: getMetricColor(activeMetric),
                  }}
                ></div>
                <span>Curr</span>
              </div>
              {yearType === 'current' && (
                <div style={styles.legendItem}>
                  <div
                    style={{
                      ...styles.legendDot,
                      backgroundColor: getLightMetricColor(activeMetric),
                    }}
                  ></div>
                  <span>Pred</span>
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              width: '100%',
              height: isMobile ? '80%' : '200px',
              minHeight: 0,
            }}
          >
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart
                data={chartData}
                margin={{ top: 16, right: 16, left: 16, bottom: 12 }}
                onClick={(e) =>
                  e && e.activeLabel && setSelectedMonth(e.activeLabel)
                }
              >
                <defs>
                  {/* GRADIENT DEFINITION: Smooth transition from Dark to Light */}
                  <linearGradient
                    id="metricGradient"
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                  >
                    <stop
                      offset="0%"
                      stopColor={getMetricColor(activeMetric)}
                    />
                    <stop
                      offset={`${splitOffset * 100}%`}
                      stopColor={getMetricColor(activeMetric)}
                    />
                    <stop
                      offset={`${splitOffset * 100}%`}
                      stopColor={getLightMetricColor(activeMetric)}
                    />
                    <stop
                      offset="100%"
                      stopColor={getLightMetricColor(activeMetric)}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  stroke="#f1f5f9"
                  vertical={false}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval={0}
                />
                <YAxis hide domain={['auto', 'auto']} />

                <Tooltip
                  cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div
                          style={{
                            backgroundColor: '#fff',
                            padding: '8px 12px',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            fontSize: '12px',
                          }}
                        >
                          <p
                            style={{
                              fontWeight: 'bold',
                              color: '#334155',
                              marginBottom: '4px',
                            }}
                          >
                            {label}
                          </p>
                          {payload.map((entry, index) => {
                            if (
                              entry.dataKey !== 'val' &&
                              entry.dataKey !== 'baseline'
                            )
                              return null;
                            let name = labelName;
                            // Check if this specific point is prediction based on payload
                            if (entry.payload.isPredicted) name += ' (Pred)';
                            if (entry.dataKey === 'baseline') name = 'Average';

                            return (
                              <div key={index} style={{ color: entry.stroke }}>
                                {name}: {Number(entry.value).toFixed(3)}
                              </div>
                            );
                          })}
                        </div>
                      );
                    }
                    return null;
                  }}
                />

                <ReferenceLine
                  x={selectedMonth}
                  stroke="#94a3b8"
                  strokeDasharray="3 3"
                />

                {riskType === 'POST' && yearType === 'current' && (
                  <Line
                    type="monotone"
                    dataKey="baseline"
                    stroke="#000000"
                    strokeWidth={2}
                    dot={false}
                    activeDot={false}
                  />
                )}

                {/* SINGLE CONTINUOUS SMOOTH LINE */}
                <Line
                  type="monotone"
                  dataKey="val"
                  stroke="url(#metricGradient)"
                  strokeWidth={3}
                  connectNulls={false}
                  activeDot={{ r: 5 }}
                  dot={({ cx, cy, payload }) => {
                    // Custom Dot: Solid for history, Hollow for prediction
                    if (payload.isPredicted) {
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={3}
                          fill="#fff"
                          stroke={getLightMetricColor(activeMetric)}
                          strokeWidth={2}
                        />
                      );
                    }
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={3}
                        fill={getMetricColor(activeMetric)}
                        stroke="none"
                      />
                    );
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          bottom: isMobile ? '5px' : '10px',
          // top: isMobile ? "4rem" : "10rem",
          top: '50%',
          right: '0',
          transform: 'translateY(-50%, -50%)',
          zIndex: 1,
          // background: "rgba(255, 255, 255, 0.9)",
          padding: isMobile ? '4px' : '8px',
          height: 'fit-content',
          // borderRadius: "8px",
          // boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
        }}
      >
        <button
          onClick={() => setIsShow(!isShow)}
          style={{ background: 'white' }}
          className="px-2 py-2 border-0 rounded fw-bold shadow-sm"
        >
          {isShow ? 'Hide Analysis' : 'Show Analysis'}
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: '650px',
    overflow: 'hidden',
  },
  backgroundLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  topHeader: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    zIndex: 20,
  },
  controlGroup: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
  },
  navButton: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#4b5563',
    display: 'flex',
    alignItems: 'center',
  },
  yearText: {
    fontWeight: 'bold',
    fontSize: '18px',
    color: '#1f2937',
    margin: '0 12px',
    minWidth: '50px',
    textAlign: 'center',
  },
  metricButton: {
    border: 'none',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  leftPanel: {
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(8px)',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
    zIndex: 20,
    border: '1px solid rgba(255,255,255,0.5)',
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '8px',
    marginBottom: '12px',
    flexDirection: 'column',
  },
  panelTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#374151',
    margin: 0,
  },
  panelDate: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#374151',
    margin: 0,
  },
  statsList: {
    flex: 1,
    //  overflowY: "auto",
    paddingRight: '4px',
  },

  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    fontWeight: '600',
    color: '#4b5563',
    marginBottom: '4px',
  },
  statLabel: { textTransform: 'capitalize' },
  statValue: { fontFamily: 'monospace', color: '#111827', fontSize: '13px' },
  progressBarBg: {
    width: '100%',
    height: '6px',
    backgroundColor: '#f3f4f6',
    borderRadius: '999px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '999px',
    transition: 'width 0.5s ease-in-out',
  },
  riskContainer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  riskCard: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '8px',
    textAlign: 'center',
  },
  riskLabel: {
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#64748b',
    marginBottom: '2px',
  },
  riskValue: { fontSize: '14px', fontWeight: 'bold' },
  graphPanel: {
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(8px)',
    borderRadius: '12px',
    padding: '12px',
    boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
    zIndex: 20,
    border: '1px solid rgba(255,255,255,0.5)',
  },
  graphHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    padding: '0 4px',
  },
  graphTitle: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#334155',
    textTransform: 'uppercase',
  },
  legendContainer: {
    display: 'flex',
    gap: '12px',
    fontSize: '10px',
    color: '#64748b',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: '4px' },
  legendDot: { width: '8px', height: '8px', borderRadius: '50%' },
};

export default RiskTypeViewer;
